import { getTheme } from '@captaincause/lib/project'
import { computeAmount } from '@captaincause/lib/amount'
import { Badge } from '../Badge'
import { Button } from '../Button'
import { Html } from '../Html'
import { Icon } from '../Icon'
import { ProjectCCertifications } from '../ProjectCCertifications'
import { ProjectImages } from '../ProjectImages'
import { ProjectLabels } from '../ProjectLabels'
import { ProjectNews } from '../ProjectNews'
import { ProjectNote } from '../ProjectNote'
import { ProjectODDs } from '../ProjectODDs'
import { ProjectRegion } from '../ProjectRegion'
import { ProjectImpactCumulated } from '../ProjectImpact/CumulatedImpact'
import { ProjectOrganizationLinks } from '../ProjectOrganizationLinks'
import { ProjectOrganizationLogo } from '../ProjectOrganizationLogo'
import { ProjectSimilarProjects } from '../ProjectSimilarProjects'
import { ProjectKeyNumbers } from '../ProjectKeyNumbers'
import { ProjectQuote } from '../ProjectQuote'
import { ProjectComments } from '../ProjectComments'
import { tm } from '../tm'
import i18n from './i18n'
import type { Locales, Project, ProjectId } from '@captaincause/types'
import type { ReactNode } from 'react'
import type { ClientSideLink } from '../Button'

export interface ProjectPageProps {
  as?: 'main' | 'div'
  CallToAction?: ReactNode | null
  className?: string
  embed?: boolean
  id?: string
  Link?: ClientSideLink
  seeMore?: (id: ProjectId) => void
  locale: Locales
  similarProjectBaseUrl?: string
  project: Project
  isDonation?: boolean
  showImages?: boolean
  showMicroImpact?: boolean
  showName?: boolean
  showOrganizationLogo?: boolean
  showCCertifications?: boolean
  showSimilarProjects?: boolean
  showAdditionalProjectInfos?: boolean
  showSourceOrganization?: boolean
  rootNode?: string
}

export const ProjectPage = ({
  as: As = 'main',
  CallToAction = null,
  className = '',
  embed = false,
  id = undefined,
  Link = undefined,
  locale,
  similarProjectBaseUrl = undefined,
  project,
  seeMore = undefined,
  isDonation = false,
  showImages = true,
  showMicroImpact = true,
  showName = true,
  showOrganizationLogo = true,
  showCCertifications = true,
  showSimilarProjects = true,
  showAdditionalProjectInfos = false,
  showSourceOrganization = true,
  rootNode = '__next',
}: ProjectPageProps) => {
  const t = i18n[locale] ?? i18n.fr

  const theme1 = project.theme1 ? getTheme(project.theme1, locale) : undefined
  const theme2 = project.theme2 ? getTheme(project.theme2, locale) : undefined

  const shouldDisplayGoal = !!(
    project.key_numbers &&
    project.key_numbers.length !== 0 &&
    project.goal
  )

  const hasGhostOrganization =
    project.source_organization.name !== project.organization.name &&
    showSourceOrganization

  return (
    <As
      id={id}
      className={tm(
        'relative mx-auto grid w-full max-w-7xl flex-1 bg-white pb-6 md:grid-cols-[1fr_1fr] md:gap-x-14 md:gap-y-8 md:px-8 md:py-10 lg:grid-cols-[1fr_416px] lg:gap-y-10 ',
        className,
      )}
    >
      {showImages ? <ProjectImages project={project} /> : null}

      {embed && project.hello_asso_url ? (
        <div className="relative mt-8 h-12 md:col-span-2 md:mt-0">
          <Button
            variant="primary"
            href={project.hello_asso_url}
            small
            className="absolute left-1/2 -translate-x-1/2"
            innerClassName="flex items-center"
          >
            {t.donation}
          </Button>
        </div>
      ) : null}
      <div className="space-y-6 px-4 md:col-start-1 md:col-end-2 md:space-y-10 md:px-0">
        {showName ? (
          <h1 className="mt-6 font-default text-2xl font-semibold md:mt-0">
            {project.name}
          </h1>
        ) : null}

        {showOrganizationLogo ? (
          <ProjectOrganizationLogo
            project={project}
            className="flex space-x-6 md:hidden"
          />
        ) : null}

        {CallToAction ? (
          <div className="block md:hidden">{CallToAction}</div>
        ) : null}

        {embed && project.organization.one_liner ? (
          <p className="mb-1 font-semibold text-black md:hidden">
            {project.organization.one_liner}
          </p>
        ) : null}
        {embed && project.organization.pitch ? (
          <Html className="md:hidden">{project.organization.pitch}</Html>
        ) : null}

        {embed && project.amount_max ? (
          <div className="flex flex-col items-center">
            <div className="mx-auto flex w-fit flex-col items-center justify-center space-y-2 rounded px-8 py-4 md:pb-8">
              <span className="text-4xl font-semibold md:text-6xl">
                {computeAmount(project.amount_max, locale)}
              </span>
              <span className="text-gray-500 text-sm">
                {t['project.amount_max']}
              </span>
            </div>
          </div>
        ) : null}

        {project.problem_addressed || project.goal ? (
          <div className={tm(!showMicroImpact && '!mt-10')}>
            <div className="mb-4 flex flex-col-reverse items-start md:flex-row md:items-center md:justify-between md:space-x-1">
              <h2 className="font-default text-base font-semibold leading-loose">
                {shouldDisplayGoal ? t.project : t.challenge}
              </h2>
              <div className="mb-6 flex w-full items-center space-x-2 md:mb-0 md:w-fit md:justify-end">
                {theme1 ? (
                  <Badge variant="gray-static">{theme1.label}</Badge>
                ) : null}
                {theme2 ? (
                  <Badge variant="gray-static">{theme2.label}</Badge>
                ) : null}
              </div>
            </div>
            {shouldDisplayGoal ? (
              <div className="text-base leading-loose text-dark-gray">
                {project.goal}
              </div>
            ) : null}
            {!shouldDisplayGoal
              ? project.problem_addressed && (
                  <Html className="text-base leading-loose text-dark-gray">
                    {project.problem_addressed}
                  </Html>
                )
              : null}
          </div>
        ) : null}

        <ProjectKeyNumbers project={project} />

        {project.pitch ? (
          <div>
            <h2 className="mb-4 font-default text-base font-semibold leading-loose">
              {t.solution}
            </h2>

            <Html className="text-base leading-loose text-dark-gray">
              {project.pitch}
            </Html>
          </div>
        ) : null}

        <ProjectComments project={project} locale={locale} />

        {project.project_updates && project.project_updates.length > 0 ? (
          <>
            <hr className="w-full text-medium-gray" />

            <ProjectNews
              project={project}
              locale={locale}
              className="!mt-6"
              rootNode={rootNode}
            />

            <hr className="w-full text-medium-gray" />
          </>
        ) : null}

        {showAdditionalProjectInfos ? (
          <>
            {project.recent_result ? (
              <div>
                <h2 className="mb-4 font-default text-base font-semibold leading-loose">
                  {t['recent-result']}
                </h2>

                <Html className="text-base leading-loose text-dark-gray">
                  {project.recent_result}
                </Html>
              </div>
            ) : null}

            {project.objective_to_do ? (
              <div className="!mt-6">
                <h2 className="mb-4 font-default text-base font-semibold leading-loose">
                  {t.goal}
                </h2>

                <Html className="text-base leading-loose text-dark-gray">
                  {project.objective_to_do}
                </Html>
              </div>
            ) : null}
          </>
        ) : null}

        {project.use_of_the_money ? (
          <div className="!mt-6">
            <h2 className="mb-4 font-default text-base font-semibold leading-loose">
              {t.money}
            </h2>

            <Html className="text-base leading-loose text-dark-gray">
              {project.use_of_the_money}
            </Html>
          </div>
        ) : null}
      </div>

      <div className="mb-8 space-y-10 px-4 md:col-start-2 md:col-end-3 md:mb-0 md:space-y-10 md:px-0">
        {CallToAction ? (
          <div className="hidden md:block">
            {isDonation ? (
              <ProjectOrganizationLogo
                project={project}
                className="mb-4 hidden space-x-6 md:flex"
              />
            ) : null}
            {CallToAction}
          </div>
        ) : null}
        {project.provider_brand?.note ? (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-light-gray p-6">
            {project.provider_brand?.logo ? (
              <img
                src={project.provider_brand?.logo}
                alt="logo"
                width={100}
                height={100}
                className="w-full max-w-[200px] py-3"
              />
            ) : null}
            <hr className="mb-4 mt-6 w-full border-medium-gray" />
            {project.provider_brand?.name ? (
              <span className="mb-4 w-full text-left text-base font-semibold leading-[22px] -tracking-[0.32px] text-[#171F5F]">
                {t['brand-like'](project?.provider_brand?.name)}
              </span>
            ) : null}
            <p className="text-sm font-light leading-5 text-[#171F5F]">
              {project.provider_brand?.note}
            </p>
          </div>
        ) : null}

        {showMicroImpact ? (
          <ProjectImpactCumulated
            project={project}
            locale={locale}
            className="first:[&>div]:mb-4"
            showOrganizationLogo={!isDonation}
          />
        ) : null}

        {embed && project.organization.one_liner ? (
          <p className="mb-1 hidden font-semibold text-black md:block">
            {project.organization.one_liner}
          </p>
        ) : null}
        {embed && project.organization.pitch ? (
          <Html className="hidden text-base leading-loose md:block">
            {project.organization.pitch}
          </Html>
        ) : null}

        <ProjectRegion
          className="hidden md:block"
          project={project}
          locale={locale}
        />
        {hasGhostOrganization ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-medium-gray p-6">
            <img
              src={project.source_organization.logo}
              alt={project.source_organization.name}
              width={56}
              height={56}
              className="h-14 rounded-lg object-contain"
            />
            <p className="text-center text-sm leading-6 text-dark-gray">
              {t['source-organization']}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold leading-7">
                {project.source_organization.name}
              </span>
              <Icon
                icon="bookmark-check"
                className="rounded bg-light-gray p-[2.5px]"
              />
            </div>
          </div>
        ) : null}
        <ProjectNote
          className={tm(!showOrganizationLogo && !CallToAction && 'md:!mt-0')}
          project={project}
          locale={locale}
        />

        <ProjectQuote project={project} />

        {showCCertifications ? (
          <ProjectCCertifications project={project} locale={locale} />
        ) : null}
        <ProjectLabels project={project} locale={locale} />
        <ProjectODDs project={project} locale={locale} />
        <ProjectOrganizationLinks project={project} locale={locale} />
      </div>

      {showSimilarProjects ? (
        <ProjectSimilarProjects
          project={project}
          baseUrl={similarProjectBaseUrl}
          locale={locale}
          Link={Link}
          seeMore={seeMore}
        />
      ) : null}

      {embed ? (
        <div className="mt-10 flex w-full items-center justify-center text-center text-xs text-dark-gray md:col-start-1 md:col-end-3">
          {t['made-by']}{' '}
          <Button
            href="https://dift.com"
            className="ml-1 text-xs underline"
            variant="raw"
          >
            Dift
          </Button>
        </div>
      ) : null}
    </As>
  )
}
